// Priority SEO page: Plastic surgery costs in Vietnam.
const cost = {
  slug: "plastic-surgery-cost-vietnam",
  metaTitle: "Plastic Surgery Costs in Vietnam | Patient Guide",
  metaDescription:
    "Understand what affects plastic surgery costs in Vietnam, what quotations may include and which additional expenses international patients should consider.",
  eyebrow: "Costs",
  h1: "Plastic Surgery Costs in Vietnam: What International Patients Should Know",
  intro:
    "Cost is one of the first questions international patients ask, but a single figure rarely tells the whole story. This guide explains what shapes a price, what a responsible quotation should include, and the extra costs that are easy to overlook.",
  summary:
    "There is no fixed price for plastic surgery in Vietnam. Cost depends on the procedure, the surgeon, the facility, anaesthesia and your individual case. An indicative quotation follows an assessment and may change if clinical findings differ. The cheapest option is not always the safest.",
  published: "December 2025",
  updated: "December 2025",
  breadcrumb: [{ name: "Costs", to: "/plastic-surgery-cost-vietnam" }],
  sections: [
    {
      id: "overview",
      h2: "Cost overview",
      paragraphs: [
        "Plastic surgery pricing in Vietnam varies widely because every case is different. Two people considering the same procedure may receive different quotations once a surgeon has assessed their anatomy, health and goals.",
        "Rather than searching for the lowest number, it is usually more useful to understand what a quotation includes, what it excludes, and how a clinic explains its pricing.",
      ],
    },
    {
      id: "why-assessment",
      h2: "Why exact prices require assessment",
      paragraphs: [
        "A meaningful price can only be confirmed after a surgeon reviews your case. Photographs and an online consultation help with an initial estimate, but an in-person examination may change the plan — and therefore the cost.",
      ],
      callout:
        "Any figure given before assessment is indicative only. A proposed treatment option and its cost may change after examination.",
    },
    {
      id: "factors",
      h2: "Factors affecting price",
      paragraphs: ["Several elements combine to produce a total price. The most common are set out below."],
      subsections: [
        { h3: "Surgeon fees", paragraphs: ["Experience with your specific procedure, complexity and operating time all influence the surgeon's fee."] },
        { h3: "Hospital or facility fees", paragraphs: ["The operating facility, theatre time and level of care affect cost. A licensed hospital setting may differ in price from a smaller cosmetic facility."] },
        { h3: "Anaesthesia fees", paragraphs: ["General anaesthesia with a dedicated anaesthetist typically costs more than local anaesthesia, and is chosen for clinical reasons rather than price."] },
        { h3: "Medical tests", paragraphs: ["Pre-operative blood tests, imaging and health screening may be required and are sometimes quoted separately."] },
        { h3: "Implants and medical devices", paragraphs: ["Breast implants and other devices vary by type and product. Documentation of the exact device should be provided."] },
        { h3: "Medication and compression garments", paragraphs: ["Pain relief, antibiotics and post-operative garments are often additional items."] },
        { h3: "Hospital nights", paragraphs: ["Some procedures require one or more nights of monitored recovery, which adds to the total."] },
        { h3: "Follow-up appointments", paragraphs: ["Reviews, suture removal and dressing changes may or may not be included in the initial quotation."] },
        { h3: "Revision complexity", paragraphs: ["Revision or corrective surgery is generally more complex than a first-time procedure and is usually priced accordingly."] },
      ],
    },
    {
      id: "quotation-includes",
      h2: "What a quotation should clearly include",
      priceTable: [
        "Surgeon fee",
        "Facility / theatre fee",
        "Anaesthesia and anaesthetist",
        "Pre-operative tests",
        "Implants or devices (where relevant)",
        "Medication and garments",
        "Hospital nights",
        "Follow-up appointments",
      ],
      paragraphs: [
        "A transparent quotation itemises what is and is not included, so you can compare like with like. If something is unclear, ask for it to be written down before you travel.",
      ],
    },
    {
      id: "questions",
      h2: "Questions to ask about a quotation",
      bullets: [
        "Is this an indicative estimate or a confirmed price after assessment?",
        "What exactly is included, and what is charged separately?",
        "Are follow-up appointments and suture removal included?",
        "What happens to the price if the plan changes after examination?",
        "How are complications or revision surgery handled and charged?",
        "In which currency is the quotation, and how is payment made?",
      ],
    },
    {
      id: "cheapest",
      h2: "Why the cheapest option may not be the best option",
      paragraphs: [
        "A very low price can reflect shorter operating time, a less experienced team, a lower-cost facility or fewer follow-up appointments. None of these are inherently wrong, but they should be understood rather than assumed.",
        "Prioritising surgeon experience, an appropriate facility and clear aftercare is usually more important than saving a small amount on the headline price.",
      ],
    },
    {
      id: "currency",
      h2: "How currency conversion may affect costs",
      paragraphs: [
        "Quotations may be provided in Vietnamese dong or another currency. Exchange rates, card fees and international transfer charges can change the final amount you pay, so confirm the currency and payment method in advance.",
      ],
    },
    {
      id: "travel",
      h2: "Travel and accommodation",
      paragraphs: [
        "Beyond the surgical quotation, budget for flights, accommodation for your recovery period, local transport and everyday expenses. The required length of stay depends on the procedure and your surgeon's advice.",
      ],
    },
  ],
  faqs: [
    { q: "How much does plastic surgery in Vietnam cost?", a: "There is no single price. Cost depends on the procedure, surgeon, facility, anaesthesia and your individual case. We do not publish fixed prices; an indicative quotation follows an assessment." },
    { q: "Is a quoted price final?", a: "An initial figure is indicative. The price may change if clinical findings differ after an in-person examination." },
    { q: "What is usually not included in a quotation?", a: "Flights, accommodation, local transport, some tests, garments, medication or follow-up may be charged separately. Always ask for an itemised quotation." },
    { q: "Does a higher price mean a better result?", a: "Not necessarily. Price reflects many factors. Surgeon experience, facility standards and aftercare matter more than the headline figure alone. Results vary and are never guaranteed." },
  ],
  sources: [
    { label: "International Society of Aesthetic Plastic Surgery (ISAPS)", url: "https://www.isaps.org/" },
    { label: "NHS — Cosmetic procedures", url: "https://www.nhs.uk/conditions/cosmetic-procedures/" },
  ],
  relatedTitle: "Related guides & procedures",
  related: [
    { to: "/plastic-surgery-safety-vietnam", title: "Is plastic surgery in Vietnam safe?", desc: "How safety is determined" },
    { to: "/how-to-choose-a-plastic-surgeon-in-vietnam", title: "How to choose a plastic surgeon", desc: "Licensing and experience" },
    { to: "/patient-journey", title: "The patient journey", desc: "Every step before you travel" },
    { to: "/revision-plastic-surgery-vietnam", title: "Revision plastic surgery", desc: "Why revision costs differ" },
  ],
};

export default cost;
